import { Box, Stack, Typography } from "@mui/material";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import type { PBJFacilityInfo, PbjModelUpsertBody } from "../../../../utils/models";
import { useSearchPbjFacilities } from "../../../PbjCalculator/hooks";
import { facilityLabel } from "../../pbjFacilityLabel";
import { applyPickedFacility } from "./predictionSetup.utils";

type CmiField = "pccPdpmAvg" | "natNursingCaseMixIndex";

// Both CMI figures are a ratio's terms, and the backend rejects anything at or below zero. Blank,
// half-typed and zero are all "not answered yet" — they hold the step rather than being coerced.
export const isCmiValid = (raw: string): boolean => {
	const parsed = Number.parseFloat(raw.trim());
	return Number.isFinite(parsed) && parsed > 0;
};

type Props = {
	draft: PbjModelUpsertBody;
	onDraftChange: (next: PbjModelUpsertBody) => void;
	// Reports whether the manual pair currently on screen could be saved. The dialog's Next gate reads
	// it, so an invalid pair stops the flow here instead of at the PUT.
	onManualCmiValidityChange: (valid: boolean) => void;
};

/**
 * Setup step 1 — the facility's CMI. The CCN search is the whole step: picking the facility links
 * the department to its CMS CASPER record, which supplies the CMI pair (and, in step 2, the
 * turnover measures) without anyone typing a number. The manual pair is a small link underneath
 * because it is the fallback for a facility the search cannot find, not the expected path.
 */
export const SetupFacilityStep: FC<Props> = ({ draft, onDraftChange, onManualCmiValidityChange }) => {
	const { t } = useTranslation();
	const [facilityInput, setFacilityInput] = useState("");
	const [pickedFacility, setPickedFacility] = useState<PBJFacilityInfo | null>(null);
	// Already-manual drafts open on the manual fields — hiding the values the user typed behind a
	// link would read as if the step were unfinished.
	const [manualOpen, setManualOpen] = useState(draft.cmiManual);
	// Per-field raw text, present only for a field the user has typed in.
	const [cmiDrafts, setCmiDrafts] = useState<Partial<Record<CmiField, string>>>({});
	const { data: facilityOptions = [], isLoading: facilityLoading } = useSearchPbjFacilities(facilityInput);

	const ccnOptions = useMemo<SystemAutoCompleteOption<string>[]>(
		() => facilityOptions.map((facility) => ({ label: facilityLabel(facility), value: facility.provnum })),
		[facilityOptions],
	);

	const ccnValue = useMemo<SystemAutoCompleteOption<string> | null>(() => {
		if (pickedFacility) return { label: facilityLabel(pickedFacility), value: pickedFacility.provnum };
		if (!draft.ccn) return null;
		return { label: draft.ccn, value: draft.ccn };
	}, [pickedFacility, draft.ccn]);

	// The merge itself is `applyPickedFacility` — pure, and tested there, because what it must NOT
	// write (`facilityId`, the PBJ submission id) is the load-bearing part.
	const handlePick = (option: SystemAutoCompleteOption<string> | null): void => {
		const facility = facilityOptions.find((candidate) => candidate.provnum === option?.value) ?? null;
		setPickedFacility(facility);
		onDraftChange(applyPickedFacility(draft, facility));
	};

	// The field's raw text, so a half-typed or cleared value survives as itself instead of being
	// coerced. The backend requires both CMI figures to be greater than zero, so silently turning bad
	// input into 0 let the flow reach Finish and fail there with a 422 the step could have caught.
	const cmiText = (field: CmiField): string => (cmiDrafts[field] !== undefined ? cmiDrafts[field] : String(draft[field]));

	const handleCmiField = (field: CmiField) => (event: React.ChangeEvent<HTMLInputElement>) => {
		const raw = event.target.value;
		setCmiDrafts((previous) => ({ ...previous, [field]: raw }));
		const parsed = Number.parseFloat(raw);
		// Only a usable number reaches the draft; anything else leaves the last good value in place
		// while the field shows what was typed and `cmiValid` holds Next shut.
		// `cmiManual` travels with the edit so the next CASPER sync cannot overwrite a hand-typed pair.
		if (isCmiValid(raw)) onDraftChange({ ...draft, [field]: parsed, cmiManual: true });
	};

	// Opening the fields IS the user choosing the manual pair — so it pins `cmiManual` right then,
	// which is also what releases the step's Next button. Waiting for a keystroke left the step
	// unfinishable for anyone who opened the fields, read the values already in them and saw no
	// reason to retype one.
	const openManual = (): void => {
		setManualOpen(true);
		onDraftChange({ ...draft, cmiManual: true });
	};

	// Both fields must hold a usable number for the pair to be saveable. Reported up on every render
	// of this step so the dialog's Next gate and the fields can never disagree.
	const manualCmiValid = isCmiValid(cmiText("pccPdpmAvg")) && isCmiValid(cmiText("natNursingCaseMixIndex"));
	// biome-ignore lint/correctness/useExhaustiveDependencies: the parent only needs the resolved verdict, which is what changes.
	useEffect(() => {
		onManualCmiValidityChange(!manualOpen || manualCmiValid);
	}, [manualOpen, manualCmiValid]);

	// ...and the way back out, for a link clicked by mistake. Without it an accidental click pins the
	// pair for good: a pinned CMI never tracks CASPER again, so even picking a facility afterwards
	// would leave the score on the national averages this step exists to replace.
	const cancelManual = (): void => {
		setManualOpen(false);
		// Drop the typed text too, so reopening the fields shows the saved pair rather than the
		// half-finished entry that was abandoned.
		setCmiDrafts({});
		onDraftChange({ ...draft, cmiManual: false });
	};

	return (
		<Stack spacing={2}>
			<Typography variant="body2" color="text.secondary">
				{t("pbj_model.setup.step.facility.help")}
			</Typography>
			<SystemAutoComplete<string>
				dataTestId="pbj-model-setup-ccn"
				fullWidth
				options={ccnOptions}
				value={ccnValue}
				onChange={(_event, option) => handlePick(option)}
				inputValue={facilityInput}
				onInputChange={(_event, value) => setFacilityInput(value)}
				loading={facilityLoading}
				textFieldProps={{
					label: t("pbj_model.config.ccn"),
					defaultHelperText: t("pbj_model.setup.step.facility.ccn_help"),
					reserveHeightForHelperText: false,
				}}
			/>
			{manualOpen ? (
				<Stack spacing={1.5}>
					{/* The fields open holding the national averages, which look like real figures. Say
					    whose they are, or the step reads as already answered. */}
					<Typography variant="body2" color="text.secondary">
						{t("pbj_model.setup.step.facility.manual_help")}
					</Typography>
					<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
						<SystemValidationTextField
							label={t("pbj_model.config.pcc_pdpm_avg")}
							inputType="number"
							fullWidth
							reserveHeightForHelperText={false}
							value={cmiText("pccPdpmAvg")}
							validation={{
								validationCallback: isCmiValid,
								helperText: { invalid: t("pbj_model.setup.step.facility.manual_invalid") },
								performValidation: "onlyAfterInitialChange",
								showValidWhenValid: "never",
							}}
							injectedProps={{ slotProps: { htmlInput: { min: 0, step: 0.01, "data-testid": "pbj-model-setup-input-pcc_pdpm_avg" } } }}
							onChange={handleCmiField("pccPdpmAvg")}
							dataTestId="pbj-model-setup-field-pcc_pdpm_avg"
						/>
						<SystemValidationTextField
							label={t("pbj_model.config.nat_nursing_cmi")}
							inputType="number"
							fullWidth
							reserveHeightForHelperText={false}
							value={cmiText("natNursingCaseMixIndex")}
							validation={{
								validationCallback: isCmiValid,
								helperText: { invalid: t("pbj_model.setup.step.facility.manual_invalid") },
								performValidation: "onlyAfterInitialChange",
								showValidWhenValid: "never",
							}}
							injectedProps={{ slotProps: { htmlInput: { min: 0, step: 0.01, "data-testid": "pbj-model-setup-input-nat_nursing_cmi" } } }}
							onChange={handleCmiField("natNursingCaseMixIndex")}
							dataTestId="pbj-model-setup-field-nat_nursing_cmi"
						/>
					</Stack>
					<Box>
						<SystemButton buttonVariant="borderless" size="small" onClick={cancelManual} noFullWidthMobile data-testid="pbj-model-setup-manual-cmi-cancel">
							{t("pbj_model.setup.step.facility.manual_cancel")}
						</SystemButton>
					</Box>
				</Stack>
			) : (
				<Box>
					<SystemButton buttonVariant="borderless" size="small" onClick={openManual} noFullWidthMobile data-testid="pbj-model-setup-manual-cmi">
						{t("pbj_model.setup.step.facility.manual_link")}
					</SystemButton>
				</Box>
			)}
		</Stack>
	);
};
